import { listFilmstripSampleTimes, } from '@/features/editor/model/timelineSnapshotTiles';
/** 本地胶片单格宽度，与服务端落盘的一格一致。 */
const LOCAL_FILMSTRIP_WIDTH_PX = 160;
/** 本地胶片单格高度，与服务端落盘的一格一致。 */
const LOCAL_FILMSTRIP_HEIGHT_PX = 90;
/** 等待视频解码或跳转到某一格的最长时间，超时就跳过这一格。 */
const LOCAL_FILMSTRIP_SEEK_TIMEOUT_MS = 2_000;
/** 本地 JPEG 质量。只给轨道临时看，不必和落盘图逐像素相同。 */
const LOCAL_FILMSTRIP_JPEG_QUALITY = 0.8;
/**
 * 按服务端同一组中点，从本地文件串行截出轨道胶片。
 * 一次只用一个视频元素，避免大文件被多次解码。失败或取消不抛错，上传继续。
 * @param input 本地地址、时长、取消信号和逐格回调
 */
export async function captureLocalFilmstripFrames(input) {
    if (isCaptureAborted(input.signal))
        return;
    /** 与服务端落盘相同的整段中点。 */
    const sampleTimes = listFilmstripSampleTimes(input.durationMs);
    if (sampleTimes.length === 0)
        return;
    /** 隐藏视频，不挂到页面上，也不占用预览那一条。 */
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    video.src = input.src;
    try {
        /** 首帧还没解码时不能跳转。 */
        const ready = await waitForVideoEvent(video, 'loadeddata', input.signal, LOCAL_FILMSTRIP_SEEK_TIMEOUT_MS);
        if (!ready || isCaptureAborted(input.signal) || video.videoWidth <= 0 || video.videoHeight <= 0)
            return;
        /** 每格都画到和服务端单格一样大的画布上。 */
        const canvas = document.createElement('canvas');
        canvas.width = LOCAL_FILMSTRIP_WIDTH_PX;
        canvas.height = LOCAL_FILMSTRIP_HEIGHT_PX;
        /** 画布上下文；浏览器不提供时放弃整次截帧。 */
        const context = canvas.getContext('2d');
        if (!context)
            return;
        for (const timeMs of sampleTimes) {
            if (isCaptureAborted(input.signal))
                return;
            /** 这一格的图片地址；跳转超时或画不出来时为空。 */
            const url = await captureFrameAt(video, context, canvas, timeMs, input.signal);
            if (!url)
                continue;
            if (isCaptureAborted(input.signal)) {
                URL.revokeObjectURL(url);
                return;
            }
            input.onFrame({ timeMs, url });
        }
    }
    finally {
        video.pause();
        video.removeAttribute('src');
        video.load();
    }
}
/**
 * 读取取消标记。包一层，避免类型收窄后把后面的检查当成死代码。
 * @param signal 本次截帧的取消信号
 * @returns 已经取消时为 true
 */
function isCaptureAborted(signal) {
    return signal.aborted;
}
/**
 * 收回本地胶片格的图片地址。同一地址只收一次。
 * @param frames 要收回的格子
 */
export function revokeLocalFilmstripFrames(frames) {
    /** 已经收回的地址，避免任务和预览握着同一份时收两次。 */
    const seen = new Set();
    frames.forEach((frame) => {
        if (!frame.url || seen.has(frame.url))
            return;
        seen.add(frame.url);
        URL.revokeObjectURL(frame.url);
    });
}
/**
 * 跳到指定时间并画成一张 JPEG。
 * @param video 正在解码本地文件的视频
 * @param context 目标画布上下文
 * @param canvas 目标画布
 * @param timeMs 这一格的时间，毫秒
 * @param signal 取消信号
 * @returns 图片地址；这一格失败时为空
 */
async function captureFrameAt(video, context, canvas, timeMs, signal) {
    /** 视频时长，秒。还没读到时按目标时间本身跳。 */
    const durationSeconds = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : timeMs / 1000;
    /** 落在最后一帧之前，避免跳到时长尽头后没有画面。 */
    const targetSeconds = Math.min(timeMs / 1000, Math.max(0, durationSeconds - 0.001));
    /** 已经停在这一格时不再等 seeked，有的浏览器不会再发。 */
    const alreadyThere = Math.abs(video.currentTime - targetSeconds) < 0.001;
    if (!alreadyThere) {
        /** 是否跳到了这一格。超时按失败跳过。 */
        const seeked = await seekVideo(video, targetSeconds, signal);
        if (!seeked || signal.aborted)
            return null;
    }
    context.drawImage(video, 0, 0, LOCAL_FILMSTRIP_WIDTH_PX, LOCAL_FILMSTRIP_HEIGHT_PX);
    return canvasToObjectUrl(canvas);
}
/**
 * 把当前播放时间改到目标秒，并等到 seeked。
 * @param video 本地视频
 * @param targetSeconds 目标时间，秒
 * @param signal 取消信号
 * @returns 跳转完成时为 true
 */
function seekVideo(video, targetSeconds, signal) {
    if (signal.aborted)
        return Promise.resolve(false);
    return new Promise((resolve) => {
        /** 是否已经结算，避免超时和 seeked 一起回调。 */
        let settled = false;
        /** 这一格的超时。 */
        const timer = window.setTimeout(() => {
            finish(false);
        }, LOCAL_FILMSTRIP_SEEK_TIMEOUT_MS);
        /**
         * 结束这次跳转。
         * @param ready 跳转是否完成
         */
        function finish(ready) {
            if (settled)
                return;
            settled = true;
            window.clearTimeout(timer);
            video.removeEventListener('seeked', onSeeked);
            signal.removeEventListener('abort', onAbort);
            resolve(ready);
        }
        /** 跳转完成。 */
        function onSeeked() {
            finish(true);
        }
        /** 调用方取消了这次截帧。 */
        function onAbort() {
            finish(false);
        }
        video.addEventListener('seeked', onSeeked);
        signal.addEventListener('abort', onAbort);
        video.currentTime = targetSeconds;
    });
}
/**
 * 等到视频发出某个事件，或超时、取消。
 * @param video 本地视频
 * @param eventName 要等的事件
 * @param signal 取消信号
 * @param timeoutMs 最长等待，毫秒
 * @returns 事件先到时为 true
 */
function waitForVideoEvent(video, eventName, signal, timeoutMs) {
    if (signal.aborted)
        return Promise.resolve(false);
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA)
        return Promise.resolve(true);
    return new Promise((resolve) => {
        /** 是否已经结算。 */
        let settled = false;
        /** 解码超时。 */
        const timer = window.setTimeout(() => {
            finish(false);
        }, timeoutMs);
        /**
         * 结束等待。
         * @param ready 视频是否已经有画面
         */
        function finish(ready) {
            if (settled)
                return;
            settled = true;
            window.clearTimeout(timer);
            video.removeEventListener(eventName, onReady);
            signal.removeEventListener('abort', onAbort);
            resolve(ready);
        }
        /** 已经能画一帧。 */
        function onReady() {
            finish(true);
        }
        /** 调用方取消了这次截帧。 */
        function onAbort() {
            finish(false);
        }
        video.addEventListener(eventName, onReady);
        signal.addEventListener('abort', onAbort);
    });
}
/**
 * 把画布导出成只在本次页面有效的 JPEG 地址。
 * @param canvas 已经画好一格的画布
 * @returns 图片地址；导出失败时为空
 */
function canvasToObjectUrl(canvas) {
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            resolve(blob ? URL.createObjectURL(blob) : null);
        }, 'image/jpeg', LOCAL_FILMSTRIP_JPEG_QUALITY);
    });
}
